import { useState } from 'react';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import formatDrugLabel from 'common/utils/formatDrugLabel';
import { mergeFillLaterItem } from 'store/thunks/workQueueThunks';

import { formatScheduledDate } from '../utils/fillLaterDates';

/**
 * "Merge with parent order" for a Fill Later item (POM-6182). Confirms, then calls
 * mergeFillLaterItem, which puts the split quantity back onto the parent line item and closes the
 * row as Merged. The backend re-checks that the parent has not shipped under a row lock; a refusal
 * arrives as a handled error (toast) and keeps the dialog open, so the operator sees why.
 */
const MergeFillLaterDialog = ({ open, onClose, item }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [submitting, setSubmitting] = useState(false);

  const handleClose = (_, reason) => {
    if (reason === 'backdropClick' || submitting) return;
    onClose();
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      const result = await dispatch(mergeFillLaterItem({ fillLaterItemId: item.fillLaterItemId }));
      if (!result) return;

      enqueueSnackbar(`Qty ${item.splitQuantity} merged back into order ${item.referenceOrderId}`, {
        variant: 'success',
      });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='xs'
      fullWidth
      aria-labelledby='MergeFillLaterDialog-title'
    >
      <DialogTitle id='MergeFillLaterDialog-title'>Merge with parent order?</DialogTitle>
      <DialogContent>
        <Typography variant='body2' sx={{ mb: 0.5 }}>
          {item.patientName} · {formatDrugLabel(item)} · Qty {item.splitQuantity}
        </Typography>
        <Typography variant='body2' color='textSecondary' sx={{ mb: 2 }}>
          The fill scheduled for {formatScheduledDate(item.scheduledFor)} is removed from the queue.
        </Typography>
        <Alert severity='warning'>
          Qty {item.splitQuantity} goes back onto order {item.referenceOrderId}, which then ships
          the full amount and its label is reprinted. This is only possible while that line item has
          not shipped.
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' color='secondary' onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <LoadingButton
          variant='contained'
          color='primary'
          loading={submitting}
          onClick={handleConfirm}
          id={`MergeFillLaterDialog-${item.fillLaterItemId}-confirm`}
        >
          Merge
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

MergeFillLaterDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  item: PropTypes.shape({
    fillLaterItemId: PropTypes.string.isRequired,
    referenceOrderId: PropTypes.string,
    patientName: PropTypes.string,
    drugName: PropTypes.string,
    strength: PropTypes.string,
    splitQuantity: PropTypes.string,
    scheduledFor: PropTypes.string,
    status: PropTypes.string,
  }).isRequired,
};

export default MergeFillLaterDialog;
